import { useRef, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { authClient } from "@/api/client";
import { useAuthStore } from "@/store/auth";
import { getDeviceId } from "@/utils/deviceId";
import { hardRefresh } from "@/utils/hardRefresh";
import { takePostLoginTarget } from "@/utils/postLoginRedirect";
import { Button } from "@/components/ui/Button";
import type { AuthUser } from "@/store/auth";

const RESEND_COOLDOWN = 60;

export function SMSVerifyPage() {
  const navigate = useNavigate();
  const { setAuth } = useAuthStore();
  const [digits, setDigits] = useState<string[]>(Array(6).fill(""));
  const [serverError, setServerError] = useState("");
  const [resendCooldown, setResendCooldown] = useState(0);
  const [resendSuccess, setResendSuccess] = useState(false);
  const refs = useRef<(HTMLInputElement | null)[]>([]);
  const username = sessionStorage.getItem("pending_username") ?? "";
  const maskedPhone = sessionStorage.getItem("pending_masked_phone") ?? "";

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const t = setTimeout(() => setResendCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [resendCooldown]);

  const resendMutation = useMutation({
    mutationFn: () => authClient.post("/auth/sms-resend/", { username }),
    onSuccess: () => {
      setResendCooldown(RESEND_COOLDOWN);
      setResendSuccess(true);
      setTimeout(() => setResendSuccess(false), 4000);
    },
  });

  const code = digits.join("");

  const mutation = useMutation({
    mutationFn: (c: string) =>
      authClient.post<{ data: { user: AuthUser; access: string; refresh: string } }>(
        "/auth/sms-verify/",
        { username, code: c, device_id: getDeviceId() }
      ),
    onSuccess: async (response) => {
      const { user, access, refresh } = response.data.data;
      setAuth(user, access, refresh);
      sessionStorage.removeItem("pending_username");
      sessionStorage.removeItem("pending_masked_phone");
      navigate(
        takePostLoginTarget() ??
          (user.role === "contractor" ? "/contractor/orders" : "/"),
      );
      await hardRefresh();
    },
    onError: (err: any) => {
      setServerError(
        err.response?.data?.errors?.non_field_errors?.[0] ?? "Nieprawidłowy kod"
      );
      setDigits(Array(6).fill(""));
      refs.current[0]?.focus();
    },
  });

  const handleChange = (i: number, val: string) => {
    const digit = val.replace(/\D/g, "").slice(-1);
    const next = [...digits];
    next[i] = digit;
    setDigits(next);
    setServerError("");
    if (digit && i < 5) refs.current[i + 1]?.focus();
    if (next.every((d) => d !== "") && digit) mutation.mutate(next.join(""));
  };

  const handleKeyDown = (i: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Backspace") {
      if (digits[i]) {
        const next = [...digits];
        next[i] = "";
        setDigits(next);
      } else if (i > 0) {
        refs.current[i - 1]?.focus();
      }
    } else if (e.key === "ArrowLeft" && i > 0) {
      refs.current[i - 1]?.focus();
    } else if (e.key === "ArrowRight" && i < 5) {
      refs.current[i + 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    if (!pasted) return;
    const next = Array(6).fill("");
    pasted.split("").forEach((d, idx) => { next[idx] = d; });
    setDigits(next);
    setServerError("");
    refs.current[Math.min(pasted.length, 5)]?.focus();
    if (pasted.length === 6) mutation.mutate(pasted);
  };

  const boxClass = (i: number) =>
    `w-11 h-14 text-center text-xl font-bold rounded-lg border-2 focus:outline-none transition-colors bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 ${
      digits[i]
        ? "border-[var(--color-primary)] dark:border-[var(--color-primary)]"
        : "border-gray-300 dark:border-gray-600 focus:border-[var(--color-primary)]"
    }`;

  return (
    <div className="w-full max-w-md">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-8">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">Weryfikacja SMS</h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-8">
          Wpisz 6-cyfrowy kod wysłany na numer{" "}
          {maskedPhone
            ? <span className="font-semibold text-gray-700 dark:text-gray-300">{maskedPhone}</span>
            : "telefonu"}.
        </p>

        <div className="flex items-center justify-center gap-2 mb-6" onPaste={handlePaste}>
          {[0, 1, 2].map((i) => (
            <input
              key={i}
              ref={(el) => { refs.current[i] = el; }}
              type="text"
              inputMode="numeric"
              maxLength={1}
              value={digits[i]}
              onChange={(e) => handleChange(i, e.target.value)}
              onKeyDown={(e) => handleKeyDown(i, e)}
              autoComplete={i === 0 ? "one-time-code" : "off"}
              autoFocus={i === 0}
              className={boxClass(i)}
            />
          ))}
          <span className="text-2xl font-bold text-gray-400 dark:text-gray-500 select-none mx-1">–</span>
          {[3, 4, 5].map((i) => (
            <input
              key={i}
              ref={(el) => { refs.current[i] = el; }}
              type="text"
              inputMode="numeric"
              maxLength={1}
              value={digits[i]}
              onChange={(e) => handleChange(i, e.target.value)}
              onKeyDown={(e) => handleKeyDown(i, e)}
              autoComplete="off"
              className={boxClass(i)}
            />
          ))}
        </div>

        {serverError && <p className="text-sm text-red-600 text-center mb-4">{serverError}</p>}
        {resendSuccess && <p className="text-sm text-green-600 text-center mb-4">Nowy kod został wysłany.</p>}

        <Button
          className="w-full"
          loading={mutation.isPending}
          disabled={code.length < 6}
          onClick={() => mutation.mutate(code)}
        >
          Zweryfikuj
        </Button>

        <button
          type="button"
          disabled={resendCooldown > 0 || resendMutation.isPending}
          className="mt-3 w-full text-sm text-[var(--color-primary)] dark:text-blue-400 hover:text-[var(--color-primary)] dark:hover:text-[var(--color-primary)] disabled:opacity-40 disabled:cursor-not-allowed"
          onClick={() => resendMutation.mutate()}
        >
          {resendCooldown > 0
            ? `Wyślij ponownie (${resendCooldown}s)`
            : "Wyślij kod ponownie"}
        </button>

        <button
          type="button"
          className="mt-2 w-full text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
          onClick={() => {
            sessionStorage.removeItem("pending_username");
            sessionStorage.removeItem("pending_masked_phone");
            navigate("/auth/login");
          }}
        >
          ← Wróć do logowania
        </button>
      </div>
    </div>
  );
}
